import * as React from 'react';
import { FC, useState, useEffect } from 'react';
import { useApi } from 'src/@context/Api';
import useRouter from 'use-react-router';
import { getQueryStringParams } from 'src/utils/URL';
import { makeStyles, createStyles } from '@material-ui/styles';
import spacing from 'src/constants/spacing';
import mediaQueries from 'src/constants/responsive';
import stylesheet, { blue } from 'src/constants/stylesheet';
import Loading from './Loading';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      maxWidth: spacing.ContentMaxWidt,
      margin: '0 auto',
      boxSizing: 'content-box',
      [mediaQueries.Mobile]: {
        padding: '0 20px',
      },
      [mediaQueries.AfterMobile]: {
        padding: '0 60px',
      }
    },
    title: {
      ...stylesheet.TitleH1,
      textAlign: 'center',
      padding: '1em 0'
    },
    resultItem: {
      borderTop: `6px solid ${blue}`,
      paddingTop: 10,

      [mediaQueries.Mobile]: {
        marginBottom: spacing.Gutter.Mobile * 2,
      },
      [mediaQueries.Tablet]: {
        marginBottom: spacing.Gutter.Tablet * 2,
      },
      [mediaQueries.Laptop]: {
        marginBottom: spacing.Gutter.Laptop * 2,
      },
      [mediaQueries.Desktop]: {
        marginBottom: spacing.Gutter.Desktop * 2,
      },
    },
    resultItemTitle: {
      ...stylesheet.TitleH3,
      position: 'relative',
    },
    resultItemSnippet: {},
    resultItemLink: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    searchNav: {
      margin: '1rem',
      display: 'flex',
      justifyContent: 'center',
    },
    changePageButton: {
      border: 'none',
      backgrounColor: 'transparent',
      padding: 0,
      margin: '0 10px',
      width: 40,
    },
    arrowLeft: {
      fill: '#C8C8C8',
    },
    arrowRight: {
      fill: '#C8C8C8',
    },
    link: {
      [mediaQueries.Mobile]: {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }
    }
  }));

type Query = {
  ETag: string;
  count: number;
  inputEncoding: string;
  outputEncoding: string;
  safe: string;
  searchTerms: string;
  startIndex: number;
  title: string;
  totalResults: number;
}

export type SearchResults = {
  ETag: string;
  items: SearchResultsItem[];
  queries: { request: Query[], nextPage?: Query[], previousPage?: Query[] };
}

type SearchResultsItem = {
  ETag: string;
  cacheId: string;
  displayLink: string;
  fileFormat: string;
  formattedUrl: string;
  htmlFormattedUrl: string;
  htmlSnippet: string;
  htmlTitle: string;
  image: string;
  kind: string;
  labels: string;
  link: string;
  mime: string;
  pagemap: { cse_thumbnail: Array<{ width: string; height: string; }> };
  cse_image: Array<{ src: string }>;
  cse_thumbnail: Array<{ width: string; height: string; src: string; }>
  snippet: string;
  title: string;
}

type Props = SearchPageDto;

const Search: FC<Props> = () => {
  const cls = useStyles();
  const { location } = useRouter();
  const { app, getSearchResults } = useApi();
  const [loading, setLoading] = useState(false);
  const [prevPage, setPrevPage] = useState<boolean>();
  const [nextPage, setNextPage] = useState<boolean>();
  const [queryString, setQueryString] = useState<string>();
  const [page, setPage] = useState(1);
  const [searchResults, setSearchResults] = useState<SearchResults>();

  const config = app && app.Config;
  const translationsItems = config && config.TranslationItems;
  const noResults = translationsItems && translationsItems.find(t => t.UID === 'search_no_results');
  const results = translationsItems && translationsItems.find(t => t.UID === 'search_results');

  useEffect(() => {
    const qs = getQueryStringParams(location.search).q;
    if (!qs) { return }

    setQueryString(qs);

  }, [location.search])

  useEffect(() => {
    if (!queryString) { return }

    setLoading(true);
    getSearchResults(queryString, page)
      .then(result => {
        setSearchResults(result);
        setNextPage(result.queries && result.queries.nextPage && result.queries.nextPage.length > 0 || false);
        setPrevPage(result.queries && result.queries.previousPage && result.queries.previousPage.length > 0 || false);
        setLoading(false);
      })

  }, [queryString, page]);

  const getNextPage = () => {
    if (!searchResults || !searchResults.queries.nextPage || searchResults.queries.nextPage.length <= 0) { return }

    const newPageCount = searchResults.queries.nextPage[0].startIndex;
    setPage(newPageCount);
  };

  const getprevPage = () => {
    if (!searchResults ||
      !searchResults.queries.previousPage ||
      searchResults.queries.previousPage.length <= 0) {
      return
    }
    const newPageCount = searchResults.queries.previousPage[0].startIndex;
    setPage(newPageCount);
  };

  const leftArrow =
    <svg
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      x="0px"
      y="0px"
      viewBox="0 0 28 25.7"
      enableBackground='new 0 0 28 25.7;'
    >
      <polygon className={cls.arrowLeft} points="12.9,0 0,12.9 12.9,25.7 12.9,20.5 28,20.5 28,5.2 12.9,5.2 " />
    </svg>

  const rightArrow =
    <svg
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      x="0px"
      y="0px"
      viewBox="0 0 28 25.7"
      enableBackground="new 0 0 28 25.7;"
    >
      <polygon className={cls.arrowRight} points="15.1,25.7 28,12.9 15.1,0 15.1,5.2 0,5.2 0,20.5 15.1,20.5 " />
    </svg>

  return (
    <section className={cls.root}>
      {loading && <Loading />}
      {!loading && searchResults &&
        <>
          <h2 className={cls.title}>
            {!searchResults.items && `${noResults && noResults.Translation || ''}`}
            {searchResults.items && `${results && results.Translation || ''} ${queryString}`}
          </h2>
          {searchResults.items && searchResults.items.map(item =>
            <article
              key={item.cacheId}
              className={cls.resultItem}
            >
              <h4 className={cls.resultItemTitle}>
                {item.title}
                <a href={item.link} className={cls.resultItemLink} target={'_blank'} />
              </h4>
              <div className={cls.resultItemSnippet}>{item.snippet}</div>
              <p>
                <a
                  className={cls.link}
                  href={item.link}
                  target={'_blank'}
                  dangerouslySetInnerHTML={{ __html: item.htmlFormattedUrl }}
                />
              </p>
            </article>
          )}
          {(nextPage || prevPage) &&
            <nav className={cls.searchNav}>
              {prevPage &&
                <button
                  type='button'
                  className={cls.changePageButton}
                  onClick={getprevPage}
                >
                  {leftArrow}
                </button>
              }
              {nextPage &&
                <button
                  type='button'
                  className={cls.changePageButton}
                  onClick={getNextPage}
                >
                  {rightArrow}
                </button>
              }
            </nav>
          }
        </>
      }
    </section>);
}

export default Search;


// WEBPACK FOOTER //
// ./src/Components/Search.tsx